import { useMemo, useState } from 'react';

import { useDiagnostics, type DiagnosticEvent } from '../state/DiagnosticsProvider.js';

interface Group {
  attemptId: string | null;
  startedAt: number;
  events: DiagnosticEvent[];
}

const PANEL_OPEN_KEY = 'soluu-harness:diagnostics-open:v1';

function readInitialOpen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PANEL_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PANEL_OPEN_KEY, value ? '1' : '0');
  } catch {
    /* quota / private mode */
  }
}

export function DiagnosticsView() {
  const { events, clear } = useDiagnostics();
  const [open, setOpen] = useState<boolean>(readInitialOpen);
  const [copied, setCopied] = useState(false);

  const groups = useMemo<Group[]>(() => groupByAttempt(events), [events]);
  const onNetworkSeen = useMemo(
    () => events.some((e) => e.type === 'attempt:on-network-observed'),
    [events],
  );

  const json = useMemo(() => JSON.stringify({ events }, null, 2), [events]);

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    writeOpen(next);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* user can fall back to Download */
    }
  };

  const onDownload = () => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soluu-diagnostics-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onClear = () => {
    if (events.length === 0) return;
    const confirmed = window.confirm(
      `Clear all ${events.length} diagnostic events? This cannot be undone.`,
    );
    if (confirmed) clear();
  };

  return (
    <section className="section diagnostics">
      <button
        type="button"
        className="diagnostics-header"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="diagnostics-chevron">{open ? '▾' : '▸'}</span>
        <span>
          Diagnostics — {events.length} event{events.length === 1 ? '' : 's'}
        </span>
      </button>
      {onNetworkSeen && (
        <div className="diagnostics-banner-on-network" role="alert">
          Device was observed in <strong>ON_NETWORK</strong> — that means it
          has already been provisioned to an owner. The harness never writes
          §113 (provisioning ID), so this is not a passing test of <em>our</em>{' '}
          WiFi; the device is simply already bound. Pick a different device for
          repeatable testing.
        </div>
      )}
      {open && (
        <div className="diagnostics-body">
          <div className="row diagnostics-actions">
            <button type="button" onClick={onCopy} disabled={events.length === 0}>
              {copied ? 'Copied!' : 'Copy as JSON'}
            </button>
            <button type="button" onClick={onDownload} disabled={events.length === 0}>
              Download .json
            </button>
            <button type="button" onClick={onClear} disabled={events.length === 0}>
              Clear
            </button>
          </div>
          {groups.length === 0 ? (
            <p className="muted">
              No diagnostic events yet. Connect to a Soluu and click Connect on
              the WiFi credentials form to populate this panel.
            </p>
          ) : (
            <ul className="diagnostics-groups">
              {groups.map((group) => (
                <DiagnosticsGroup key={group.attemptId ?? 'pre'} group={group} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function DiagnosticsGroup({ group }: { group: Group }) {
  const isPre = group.attemptId === null;
  const startedLabel = isPre
    ? 'Pre-attempt / library'
    : new Date(group.startedAt).toLocaleTimeString();
  const start = group.events.find((e) => e.type === 'attempt:start');
  const summaryPayload = start?.payload as
    | { ssid?: string; deviceName?: string | null }
    | undefined;
  return (
    <li className="diagnostics-group">
      <div className="diagnostics-group-header">
        <strong>{startedLabel}</strong>
        {summaryPayload?.ssid && (
          <span className="muted"> · {summaryPayload.ssid}</span>
        )}
        {summaryPayload?.deviceName && (
          <span className="muted"> · {summaryPayload.deviceName}</span>
        )}
        <span className="muted"> · {group.events.length} events</span>
      </div>
      <ol className="diagnostics-events">
        {group.events.map((event) => (
          <DiagnosticsRow key={event.id} event={event} />
        ))}
      </ol>
    </li>
  );
}

function DiagnosticsRow({ event }: { event: DiagnosticEvent }) {
  const [open, setOpen] = useState(false);
  const isError = isErrorEvent(event);
  const time = new Date(event.timestampMs)
    .toISOString()
    .substring(11, 23); // HH:mm:ss.SSS
  const summary = summarizePayload(event);
  return (
    <li className={`diagnostics-row ${isError ? 'diagnostics-row-error' : ''}`}>
      <button
        type="button"
        className="diagnostics-row-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="diagnostics-time">{time}</span>
        <span className="diagnostics-source">{event.source}</span>
        <span className="diagnostics-type">{event.type}</span>
        {summary && <span className="diagnostics-summary muted">— {summary}</span>}
      </button>
      {open && (
        <pre className="diagnostics-payload">
          {JSON.stringify(event.payload, null, 2)}
        </pre>
      )}
    </li>
  );
}

/**
 * Which rows render error-styled. Historically this was just
 * `type.endsWith(':error')`, but ble-core's tolerant discovery tier reports a
 * miss as `characteristic:missing` rather than an error, because the connect
 * deliberately survives it.
 *
 * A miss on an *expected* characteristic is still a fault — firmware
 * registers those unconditionally, so an absent one is a real finding and
 * must not read as routine noise. A *conditional* miss (§2A25 blocklisted by
 * Chrome, §315 predating fw_rev 0.250514) has a known non-fault explanation
 * and stays unstyled.
 */
function isErrorEvent(event: DiagnosticEvent): boolean {
  if (event.type.endsWith(':error')) return true;
  if (event.type === 'characteristic:missing') {
    const p = event.payload as Record<string, unknown> | null;
    return p?.severity === 'expected';
  }
  return false;
}

function summarizePayload(event: DiagnosticEvent): string | null {
  const p = event.payload as Record<string, unknown> | null;
  if (!p || typeof p !== 'object') return null;
  switch (event.type) {
    case 'characteristic:missing':
      return [
        p.name ? String(p.name) : null,
        p.charUuid ? `(${String(p.charUuid)})` : null,
        p.severity ? `— ${String(p.severity)},` : '—',
        'NOT FOUND',
        p.error ? `· ${String(p.error)}` : null,
      ]
        .filter(Boolean)
        .join(' ');
    case 'wifiError:value':
      // Both forms, always. The decimal compares against anything numeric a
      // tester has; the hex matches the firmware's own 0x%08lX serial log.
      return [
        typeof p.value === 'number' ? `error=${p.value}` : null,
        typeof p.valueHex === 'string' ? p.valueHex : null,
        p.rawHex ? `raw=0x${String(p.rawHex)}` : null,
        typeof p.byteLength === 'number' ? `bytes=${p.byteLength}` : null,
        p.decodeError ? `decodeError=${String(p.decodeError)}` : null,
      ]
        .filter(Boolean)
        .join(' ');
    case 'notify:value':
      return [
        p.label ? `label=${p.label}` : null,
        p.rawHex ? `raw=0x${p.rawHex}` : null,
        p.decodeError ? `decodeError=${p.decodeError}` : null,
      ]
        .filter(Boolean)
        .join(' ');
    case 'write:start':
    case 'write:ok':
    case 'write:error': {
      // Password redaction is enforced at the ble-core layer: BOTH valueHex
      // AND valuePreview are null for password writes. Show "(redacted)" so
      // a reader knows the value isn't missing, it's hidden on purpose.
      const value =
        p.label === 'password'
          ? '(redacted)'
          : typeof p.valuePreview === 'string'
            ? `value=${JSON.stringify(p.valuePreview)}`
            : typeof p.valueHex === 'string'
              ? `hex=0x${p.valueHex}`
              : null;
      return [
        p.label ? `label=${p.label}` : null,
        typeof p.byteLength === 'number' ? `bytes=${p.byteLength}` : null,
        value,
        p.error ? `error=${p.error}` : null,
      ]
        .filter(Boolean)
        .join(' ');
    }
    case 'service:resolved':
    case 'service:error':
      return p.serviceUuid ? String(p.serviceUuid) : null;
    case 'characteristic:resolved':
    case 'characteristic:error':
    case 'notify:subscribed':
    case 'notify:error':
      return p.charUuid ? String(p.charUuid) : null;
    case 'attempt:start':
      return [
        p.mode ? `mode=${p.mode}` : null,
        p.ssid ? `ssid=${p.ssid}` : null,
      ]
        .filter(Boolean)
        .join(' ');
    case 'attempt:end':
      return [
        p.result ? `result=${p.result}` : null,
        typeof p.transitionsCount === 'number'
          ? `transitions=${p.transitionsCount}`
          : null,
      ]
        .filter(Boolean)
        .join(' ');
    case 'transition:appended':
    case 'transition:deduped':
      return [
        p.label ? `label=${p.label}` : null,
        typeof p.tMs === 'number' ? `tMs=${p.tMs}` : null,
      ]
        .filter(Boolean)
        .join(' ');
    case 'attempt:on-network-observed':
      return typeof p.raw === 'number'
        ? `raw=0x${p.raw.toString(16).padStart(2, '0')}`
        : null;
    default:
      return null;
  }
}

function groupByAttempt(events: readonly DiagnosticEvent[]): Group[] {
  const groups = new Map<string, Group>();
  const preGroup: Group = { attemptId: null, startedAt: 0, events: [] };
  for (const event of events) {
    if (event.attemptId === null) {
      preGroup.events.push(event);
      continue;
    }
    let group = groups.get(event.attemptId);
    if (!group) {
      group = {
        attemptId: event.attemptId,
        startedAt: event.timestampMs,
        events: [],
      };
      groups.set(event.attemptId, group);
    }
    group.events.push(event);
    if (event.type === 'attempt:start') {
      group.startedAt = event.timestampMs;
    }
  }
  const out: Group[] = [];
  if (preGroup.events.length > 0) out.push(preGroup);
  // Sort attempt groups by startedAt descending (most recent first).
  const sorted = [...groups.values()].sort((a, b) => b.startedAt - a.startedAt);
  out.push(...sorted);
  return out;
}
